import React, { useState } from 'react';
import { Modal } from 'antd';
import { Button } from '../button';
import { SaveButton } from '../action-buttons';
import { DifferenceMap } from '../../helpers/store-helper';
import style from './style.module.scss';

type CoordSuspect = { lat?: number; lng?: number };

type CoordPrecisionModalProps = {
    suspects: Map<string, CoordSuspect>;
    differences: DifferenceMap;
    onConfirm: (keepDbNrefs: Set<string>) => void;
    onGoBack: () => void;
};

type Choice = 'db' | 'csv';

export const CoordPrecisionModal = ({
    suspects,
    differences,
    onConfirm,
    onGoBack,
}: CoordPrecisionModalProps) => {
    const [choices, setChoices] = useState<Map<string, Choice>>(
        () => new Map([...suspects.keys()].map((k) => [k, 'db']))
    );

    const setAll = (choice: Choice) => {
        setChoices(new Map([...suspects.keys()].map((k) => [k, choice])));
    };

    const toggle = (nref: string) => {
        setChoices((prev) => {
            const next = new Map(prev);
            next.set(nref, prev.get(nref) === 'db' ? 'csv' : 'db');
            return next;
        });
    };

    const handleConfirm = () => {
        const keepDb = new Set(
            [...choices.entries()]
                .filter(([, choice]) => choice === 'db')
                .map(([nref]) => nref)
        );
        onConfirm(keepDb);
    };

    const dbCount = [...choices.values()].filter((c) => c === 'db').length;
    const csvCount = suspects.size - dbCount;

    return (
        <Modal
            open
            title="Rounded coordinates detected"
            footer={null}
            onCancel={onGoBack}
            width={560}
        >
            <div className={style.content}>
                <p className={style.description}>
                    <b>
                        {suspects.size} store{suspects.size > 1 ? 's' : ''}
                    </b>{' '}
                    have coordinates that differ slightly from the current
                    value. These differences are under 1 metre and are almost
                    certainly caused by floating-point precision loss during CSV
                    export — not intentional edits. Select which value to keep
                    for each store before continuing.
                </p>

                <div className={style.bulk_actions}>
                    <Button
                        variant={
                            dbCount === suspects.size ? 'primary' : 'secondary'
                        }
                        onClick={() => setAll('db')}
                    >
                        Keep all (current value)
                    </Button>
                    <Button
                        variant={
                            csvCount === suspects.size ? 'primary' : 'secondary'
                        }
                        onClick={() => setAll('csv')}
                    >
                        Keep all (CSV)
                    </Button>
                </div>

                <div className={style.store_list}>
                    {[...suspects.entries()].map(([nref, suspect]) => {
                        const diff = differences.get(nref);
                        const choice = choices.get(nref) ?? 'db';
                        const addr = diff?.initial.addr;
                        return (
                            <div key={nref} className={style.store_row}>
                                <div className={style.store_header}>
                                    <div>
                                        <b className={style.nref}>{nref}</b>
                                        {addr && (
                                            <span className={style.addr}>
                                                {addr.street}, {addr.city}
                                            </span>
                                        )}
                                    </div>
                                    <Button
                                        variant={
                                            choice === 'db'
                                                ? 'primary'
                                                : 'secondary'
                                        }
                                        onClick={() => toggle(nref)}
                                    >
                                        {choice === 'db'
                                            ? 'Keep current'
                                            : 'Keep CSV'}
                                    </Button>
                                </div>
                                <div className={style.coord_lines}>
                                    {suspect.lat !== undefined && (
                                        <div className={style.coord_line}>
                                            <span className={style.label}>
                                                Lat
                                            </span>
                                            <span className={style.db_value}>
                                                {diff?.initial.lat}
                                            </span>
                                            <span className={style.arrow}>
                                                →
                                            </span>
                                            <span
                                                className={`${
                                                    style.csv_value
                                                } ${
                                                    choice === 'csv'
                                                        ? style.active
                                                        : style.muted
                                                }`}
                                            >
                                                {suspect.lat}
                                            </span>
                                        </div>
                                    )}
                                    {suspect.lng !== undefined && (
                                        <div className={style.coord_line}>
                                            <span className={style.label}>
                                                Lng
                                            </span>
                                            <span className={style.db_value}>
                                                {diff?.initial.lng}
                                            </span>
                                            <span className={style.arrow}>
                                                →
                                            </span>
                                            <span
                                                className={`${
                                                    style.csv_value
                                                } ${
                                                    choice === 'csv'
                                                        ? style.active
                                                        : style.muted
                                                }`}
                                            >
                                                {suspect.lng}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className={style.footer}>
                    <Button variant="secondary" onClick={onGoBack}>
                        Go back
                    </Button>
                    <SaveButton onClick={handleConfirm}>Continue</SaveButton>
                </div>
            </div>
        </Modal>
    );
};
